const PHOTO_ONBOARDING_TRACKING_PATH =
  "/fahrzeug-profil/photo-onboarding-tracking";
const PENDING_STORAGE_KEY = "ael-photo-onboarding-tracking-pending-v1";
const DEFAULT_RETRY_DELAYS_MS = [0, 250];
const DEFAULT_BACKGROUND_RETRY_DELAYS_MS = [1500, 5000, 15000];
const AUTH_HYDRATION_TIMEOUT_MS = 4000;

export const PHOTO_ONBOARDING_ACTIONS = Object.freeze({
  STEP_1_REACHED: "step_1_reached",
  STEP_2_REACHED: "step_2_reached",
  STEP_3_REACHED: "step_3_reached",
  STEP_4_REACHED: "step_4_reached",
  CAMERA_TEST_REACHED: "camera_test_reached",
  CAMERA_WORKING_CLICKED: "camera_working_clicked",
  CAMERA_NOT_WORKING_CLICKED: "camera_not_working_clicked",
  // "Ich habe bereits Fotos" auf dem Zeit-Screen: Aufklärungsseite geöffnet
  // bzw. danach mit "Verstanden, los geht's" fortgefahren.
  EXISTING_PHOTOS_CLICKED: "existing_photos_clicked",
  EXISTING_PHOTOS_CONTINUED: "existing_photos_continued",
  // Legacy-Action bewusst weiterhin akzeptieren, damit bereits lokal
  // vorgemerkte Events aus dem ersten Rollout noch zugestellt werden koennen.
  REACHED: "reached",
  CONTINUED_CAMERA_WORKING: "continued_camera_working",
  CONTINUED_WITHOUT_APP: "continued_without_app",
  APP_DOWNLOAD_CLICKED: "app_download_clicked",
});

const VALID_ACTIONS = new Set(Object.values(PHOTO_ONBOARDING_ACTIONS));
const inFlightRequests = new Map();
const deliveredRequests = new Set();
const backgroundRetryStates = new Map();

function requestKey(payload) {
  return `${payload.vehicleId}|${payload.action}`;
}

export function createPhotoOnboardingTrackingPayload({ vehicleId, action }) {
  const normalizedVehicleId = String(vehicleId || "").trim();
  const normalizedAction = String(action || "").trim();

  if (!normalizedVehicleId || !VALID_ACTIONS.has(normalizedAction)) {
    return null;
  }

  return { vehicleId: normalizedVehicleId, action: normalizedAction };
}

function readPendingRequests() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PENDING_STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(createPhotoOnboardingTrackingPayload)
      .filter(Boolean);
  } catch {
    return [];
  }
}

function writePendingRequests(requests) {
  try {
    if (requests.length === 0) {
      localStorage.removeItem(PENDING_STORAGE_KEY);
      return;
    }
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(requests));
  } catch {
    // Der direkte Request funktioniert auch, wenn localStorage blockiert ist.
  }
}

function enqueuePendingRequest(payload) {
  const pending = readPendingRequests();
  const key = requestKey(payload);
  if (!pending.some((item) => requestKey(item) === key)) {
    pending.push(payload);
    writePendingRequests(pending);
  }
}

function removePendingRequest(payload) {
  const key = requestKey(payload);
  writePendingRequests(
    readPendingRequests().filter((item) => requestKey(item) !== key),
  );
}

function waitForPromise(promise, timeoutMs) {
  let timeoutId;
  return Promise.race([
    Promise.resolve(promise).then(
      () => true,
      () => false,
    ),
    new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve(false), timeoutMs);
    }),
  ]).finally(() => clearTimeout(timeoutId));
}

function waitForAuthStateCallback(auth, onAuthStateChanged, timeoutMs) {
  if (typeof onAuthStateChanged !== "function" || timeoutMs <= 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let unsubscribe;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      if (typeof unsubscribe === "function") unsubscribe();
      resolve();
    };
    const timeoutId = setTimeout(finish, timeoutMs);

    try {
      unsubscribe = onAuthStateChanged(auth, finish, finish);
      // Manche Test-/Fallback-Implementierungen rufen synchron zurueck, bevor
      // unsubscribe zugewiesen wurde. In diesem Fall danach sauber abmelden.
      if (settled && typeof unsubscribe === "function") unsubscribe();
    } catch {
      finish();
    }
  });
}

/** Wartet auf den initialen Firebase-Auth-State, bevor currentUser gelesen wird.
 * authStateReady ist der primaere Weg; der Callback deckt aeltere SDKs ab. */
export async function getIdTokenAfterAuthReady(
  auth,
  {
    onAuthStateChanged,
    timeoutMs = AUTH_HYDRATION_TIMEOUT_MS,
    forceRefresh = false,
  } = {},
) {
  if (!auth) return null;

  const deadline = Date.now() + Math.max(0, timeoutMs);
  if (!auth.currentUser && typeof auth.authStateReady === "function") {
    const ready = await waitForPromise(
      auth.authStateReady(),
      Math.max(0, deadline - Date.now()),
    );
    // Ein erfolgreich aufgeloester Auth-State ist auch dann final, wenn kein
    // User angemeldet ist. Credentials bleiben als Session-Fallback im Request.
    if (!ready) {
      await waitForAuthStateCallback(
        auth,
        onAuthStateChanged,
        Math.max(0, deadline - Date.now()),
      );
    }
  } else if (!auth.currentUser) {
    await waitForAuthStateCallback(
      auth,
      onAuthStateChanged,
      Math.max(0, deadline - Date.now()),
    );
  }

  try {
    return (await auth.currentUser?.getIdToken(forceRefresh)) || null;
  } catch {
    return null;
  }
}

export async function getCurrentIdToken(forceRefresh = false) {
  try {
    const { getFirebaseAuth } = await import("@/lib/firebaseClient");
    const { onAuthStateChanged } = await import("firebase/auth");
    const auth = await getFirebaseAuth();
    return getIdTokenAfterAuthReady(auth, {
      onAuthStateChanged,
      forceRefresh,
    });
  } catch {
    return null;
  }
}

function wait(ms) {
  if (!ms) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function sendWithRetry(
  apiBase,
  payload,
  {
    fetchImpl = globalThis.fetch,
    getIdToken = getCurrentIdToken,
    retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
  } = {},
) {
  if (typeof fetchImpl !== "function") return false;

  for (const delayMs of retryDelaysMs) {
    await wait(delayMs);
    try {
      const idToken = await getIdToken();
      const response = await fetchImpl(
        `${String(apiBase).replace(/\/+$/, "")}${PHOTO_ONBOARDING_TRACKING_PATH}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
          credentials: "include",
          keepalive: true,
          body: JSON.stringify(payload),
        },
      );
      if (response.ok) return true;
    } catch {
      // Der naechste Versuch bzw. die persistierte Queue uebernimmt.
    }
  }

  return false;
}

function clearBackgroundRetry(payload) {
  const key = requestKey(payload);
  const state = backgroundRetryStates.get(key);
  if (state?.timerId) clearTimeout(state.timerId);
  backgroundRetryStates.delete(key);
}

function scheduleBackgroundRetry(apiBase, payload, options) {
  const key = requestKey(payload);
  const retryDelays =
    options?.backgroundRetryDelaysMs || DEFAULT_BACKGROUND_RETRY_DELAYS_MS;
  if (!Array.isArray(retryDelays) || retryDelays.length === 0) return;

  const state = backgroundRetryStates.get(key) || {
    nextAttempt: 0,
    timerId: null,
  };
  if (state.timerId || state.nextAttempt >= retryDelays.length) return;

  const delayMs = Math.max(0, Number(retryDelays[state.nextAttempt]) || 0);
  state.nextAttempt += 1;
  state.timerId = setTimeout(() => {
    state.timerId = null;
    // deliverRequest plant bei erneutem Fehlschlag den naechsten Backoff. Ist
    // parallel bereits ein manueller Flush aktiv, wird dessen Promise genutzt.
    void deliverRequest(apiBase, payload, options);
  }, delayMs);
  backgroundRetryStates.set(key, state);
}

async function deliverRequest(apiBase, payload, options) {
  const key = requestKey(payload);
  if (deliveredRequests.has(key)) {
    // Ein spaeterer React-Mount kann dieselbe idempotente Action erneut
    // vormerken. Da sie in dieser Session bereits bestaetigt wurde, darf kein
    // verwaister Queue-Eintrag zurueckbleiben.
    removePendingRequest(payload);
    clearBackgroundRetry(payload);
    return true;
  }
  if (inFlightRequests.has(key)) return inFlightRequests.get(key);

  const request = sendWithRetry(apiBase, payload, options)
    .then((delivered) => {
      if (delivered) {
        deliveredRequests.add(key);
        removePendingRequest(payload);
        clearBackgroundRetry(payload);
      } else {
        scheduleBackgroundRetry(apiBase, payload, options);
      }
      return delivered;
    })
    .finally(() => inFlightRequests.delete(key));

  inFlightRequests.set(key, request);
  return request;
}

/**
 * Persistiert das Ereignis zuerst lokal und sendet es anschliessend idempotent.
 * Fehler werden als `false` zurueckgegeben und blockieren den Foto-Flow nicht.
 */
export function trackPhotoOnboarding(apiBase, tracking, options) {
  const payload = createPhotoOnboardingTrackingPayload(tracking || {});
  if (!apiBase || !payload) return Promise.resolve(false);

  enqueuePendingRequest(payload);
  return deliverRequest(apiBase, payload, options);
}

/** Wiederholt noch offene Events, z.B. nach Navigation oder wenn das Geraet
 * wieder online ist. Der Backend-Endpunkt ist fuer Wiederholungen idempotent. */
export async function flushPendingPhotoOnboardingTracking(apiBase, options) {
  if (!apiBase) return [];
  const pending = readPendingRequests();
  return Promise.all(
    pending.map((payload) => deliverRequest(apiBase, payload, options)),
  );
}

/** Wartet nur kurz auf den primaeren Tracking-Versuch. Der Request laeuft mit
 * keepalive weiter, falls die Navigation nach dem Zeitlimit bereits startet. */
export async function waitForPhotoOnboardingTracking(
  trackingPromise,
  maxWaitMs = 900,
) {
  let timeoutId;
  try {
    await Promise.race([
      Promise.resolve(trackingPromise),
      new Promise((resolve) => {
        timeoutId = setTimeout(resolve, maxWaitMs);
      }),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
}
